import { z } from "zod";
import { Privacy } from "@/helpers/constants";
import { PrivacyLevel } from "@/types";

/**
 * A workspace's sharing mode plus its grant set.
 *
 * `privacy` includes `restricted` here, unlike the knowledge-catalog schemas
 * that stay two-value — only workspaces have a grant list behind them.
 */
export const DatamateSharesSchema = z.object({
  datamate_id: z.number(),
  privacy: z.enum([Privacy.PRIVATE, Privacy.PUBLIC, Privacy.RESTRICTED]),
  user_ids: z.number().array(),
  team_ids: z.number().array(),
});
export type DatamateShares = z.infer<typeof DatamateSharesSchema>;

/** A connection's sharing mode plus its grant set — the workspace shape. */
export const ConnectionSharesSchema = z.object({
  connection_id: z.number(),
  privacy: z.enum([Privacy.PRIVATE, Privacy.PUBLIC, Privacy.RESTRICTED]),
  user_ids: z.number().array(),
  team_ids: z.number().array(),
});
export type ConnectionShares = z.infer<typeof ConnectionSharesSchema>;

/** What a sharing form reads, whichever resource it shares. */
export type ResourceShares = Pick<DatamateShares, "privacy" | "user_ids" | "team_ids">;

export interface DatamateSharesPayload {
  privacy: PrivacyLevel;
  user_ids?: number[];
  team_ids?: number[];
}

/**
 * Mirrors `MAX_SHARE_GRANTS` on the API. Bounds what one payload may name *and*
 * what a workspace may accumulate — the endpoint replaces the whole set, so an
 * unbounded set eventually cannot be sent back in full.
 */
export const MAX_SHARE_GRANTS = 100;

/**
 * How to name a grantee in copy, given whether teams can be granted at all.
 *
 * Teams are user groups and exist only under RBAC, so a tenant without it must
 * not be told to "choose people or teams" — there is no teams picker on screen
 * to act on.
 */
export const granteeNoun = (allowTeams: boolean) => ({
  singular: allowTeams ? "person or team" : "person",
  plural: allowTeams ? "people and teams" : "people",
});
